import DocumentTypeCombobox from "@/components/custom/DocumentTypeCombobox";
import FileDropzone from "@/components/custom/FileDropzone";
import ProjectCombobox from "@/components/custom/ProjectCombobox";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { useAuth } from "@/auth/hooks/useAuth";
import { useTabDashboard } from "@/dashboard/store/store";
import {
  applyRememberedDocumentComments,
  rememberDocumentComments,
  withDocumentComment,
} from "@/documents/utils/documentComments";
import {
  showDocumentUploadErrorToast,
  showDocumentUploadInProgressToast,
  showDocumentUploadSuccessToast,
} from "@/documents/utils/downloadToast";
import { getDocumentTypeLabel } from "@/documents/utils/documentTree";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import useImageCompressor from "@/hooks/useImageCompressor";
import { useMutateData } from "@/hooks/useMutation";
import usePostMultipart from "@/hooks/usePostMultipart";
import { ProgramType, TreeRoot } from "@/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "@/components/ui/use-toast";

const PROGRAM_ROOT_DEFAULT_KEY_PREFIX = "program-";
const ROOT_FILES_DEFAULT_KEY = "root-files";
const PROJECT_DOC_TYPES = [2, 3, 7, 8];
const INTERVENANT_DOC_TYPES = [1, 5, 4];
const CONTRACTUAL_DOC_TYPES = [25, 26, 27];
const REPORT_DOC_TYPES = [10, 11, 12, 13, 14, 15];
const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png"];
const PORTEUR_ROOT_DEFAULT_LABEL = "Documents administratifs";

type DocumentFolderTreeResponse = {
  porteur?: Record<string, FolderNode[]>;
  project?: Record<string, FolderNode[]>;
  intervenant?: Record<string, FolderNode[]>;
};

type FolderNode = {
  id: number;
  name?: string | null;
  defaultKey?: string | null;
  children?: FolderNode[];
};

type ResolvedDefaultFolderTarget = {
  scopeType: "porteur" | "project" | "intervenant";
  scopeId: number;
  defaultKey: string;
  folderName: string;
};

type DocumentFolderMeta = {
  id: number;
  name?: string | null;
  defaultKey?: string | null;
  scopeType?: "porteur" | "project" | "intervenant";
  scopeId?: number;
};

type ProjectOption = {
  label: string;
  groupLabel: string;
  groupId: number;
  value: number;
  media: Array<{ id: number }>;
};

type CacheSnapshot = Array<{
  queryKey: readonly unknown[];
  data: unknown;
}>;

type AddDocumentsProps = {
  externalFiles?: File[];
  forceProjectSelection?: boolean;
  hideTrigger?: boolean;
  onExternalFilesConsumed?: () => void;
  showProjectSelection?: boolean;
};

const getAuthHeaders = (): HeadersInit => {
  const token =
    localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fetchJsonWithAuth = async <T,>(endpoint: string): Promise<T | null> => {
  const response = await fetch(endpoint, {
    headers: getAuthHeaders(),
    credentials: "include",
  });

  if (!response.ok) {
    return null;
  }

  return response.json();
};

const getFirstPositiveNumberParam = (params: URLSearchParams, key: string): number | null => {
  const value = Number(params.get(key)?.split(",")[0] ?? 0);

  return Number.isFinite(value) && value > 0 ? value : null;
};

const AddDocuments = ({
  externalFiles,
  forceProjectSelection = false,
  hideTrigger = false,
  onExternalFilesConsumed,
  showProjectSelection = false,
}: AddDocumentsProps) => {
  const { user } = useAuth();
  const [openAdd, setOpenAdd] = useState(false);
  const [documentType, setDocumentType] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [loader, setLoader] = useState(false);
  const [project, setProject] = useState<ProjectOption[]>([]);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [documentFolderId, setDocumentFolderId] = useState<number | null>(null);
  const { tab } = useTabDashboard();
  const carrierDocumentsEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/docs/carrier`;
  const documentFoldersTreeEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/document-folders/tree`;
  const { data: carrierTree } = useFetchDataV2<TreeRoot[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/tree/carrier`,
    `tree-carrier-add-documents`,
    1000 * 60 * 60,
  );
  const { data: customFolders } = useFetchDataV2<DocumentFolderTreeResponse>(
    documentFoldersTreeEndpoint,
    `document-folders-tree-add-documents`,
    1000 * 60 * 60,
  );

  const { data, loading, error } = useFetchDataV2<ProgramType[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/info/programs`,
    `programsInfoTree`,
    1000 * 60 * 60,
  );
  useEffect(() => {
    if (data && data.length > 0 && !loading && !error) {
      const flatOptions = data.flatMap((group: any) =>
        group?.projects?.map((child: any) => ({
          label: child.name,
          groupLabel: group.name,
          groupId: Number(group?.id),
          value: child.id,
          media:
            child?.mediaDescription?.length > 0 && child?.mediaDescription
              ? child?.mediaDescription
              : [],
        })) ?? [],
      );
      setProject(flatOptions);
    }
  }, [data, error, loading]);

  const scopedProjects = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const programIds = (params.get("programs") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (programIds.length !== 1) {
      return project;
    }

    return project.filter(
      (projectOption: any) => Number(projectOption?.groupId) === programIds[0],
    );
  }, [project]);

  useEffect(() => {
    const urlParts = window.location.pathname.split("/");
    const projectId1 = Number(urlParts[3]); // ["", "dashboard", "project", "584"]
    if (Number.isFinite(projectId1) && projectId1 > 0) setProjectId(projectId1);
  }, []);

  const textTypes = "PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG";
  const { postMultipart } = usePostMultipart();
  const { handleCompressImage } = useImageCompressor();
  const { mutateAsync: addFile } = useMutateData({
    method: "POST",
    id: `docs-carrier`,
  });
  const { mutateAsync: saveFolderAsync } = useMutateData({
    method: "POST",
    id: `document-folder-save`,
  });

  const queryClient = useQueryClient();
  const carrierOwnerId = useMemo(() => {
    const authUserId = Number(user?.id ?? 0) || null;

    if (!carrierTree || carrierTree.length === 0) {
      return authUserId;
    }

    if (authUserId && carrierTree.some((carrier) => Number(carrier.id) === authUserId)) {
      return authUserId;
    }

    return Number(carrierTree[0]?.id ?? 0) || authUserId;
  }, [carrierTree, user]);

  const findFolderByDefaultKey = (folders: FolderNode[] = [], defaultKey: string): FolderNode | null => {
    for (const folder of folders) {
      if ((folder.defaultKey ?? null) === defaultKey) {
        return folder;
      }

      const nested = findFolderByDefaultKey(folder.children ?? [], defaultKey);
      if (nested) {
        return nested;
      }
    }

    return null;
  };

  const findFolderById = (folders: FolderNode[] = [], folderId: number): FolderNode | null => {
    for (const folder of folders) {
      if (Number(folder.id) === folderId) {
        return folder;
      }

      const nested = findFolderById(folder.children ?? [], folderId);
      if (nested) {
        return nested;
      }
    }

    return null;
  };

  const resolveFolderMeta = (
    folderId: number | null,
    target?: ResolvedDefaultFolderTarget | null,
  ): DocumentFolderMeta | null => {
    if (!folderId) {
      return null;
    }

    if (target) {
      return {
        id: folderId,
        name: target.folderName,
        defaultKey: target.defaultKey,
        scopeType: target.scopeType,
        scopeId: target.scopeId,
      };
    }

    const scopeTypes: Array<"porteur" | "project" | "intervenant"> = ["porteur", "project", "intervenant"];

    for (const scopeType of scopeTypes) {
      const foldersByScope = customFolders?.[scopeType] ?? {};

      for (const [scopeId, folders] of Object.entries(foldersByScope)) {
        const folder = findFolderById(folders, folderId);
        if (folder) {
          return {
            id: folderId,
            name: folder.name,
            defaultKey: folder.defaultKey,
            scopeType,
            scopeId: Number(scopeId),
          };
        }
      }
    }

    return { id: folderId };
  };

  const hasSameValues = (values: number[], expectedValues: number[]) => {
    const left = [...new Set(values)].sort((a, b) => a - b);
    const right = [...new Set(expectedValues)].sort((a, b) => a - b);

    return left.length === right.length && left.every((value, index) => value === right[index]);
  };

  const resolveCurrentDefaultFolderTarget = (): ResolvedDefaultFolderTarget | null => {
    const params = new URLSearchParams(window.location.search);
    const scope = params.get("scope");
    const projectIds = (params.get("projects") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const programIds = (params.get("programs") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const intervenantIds = (params.get("intervenants") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const categoryIds = (params.get("categories") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const selectedTypes = (params.get("types") ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    const currentOwnerId = Number(carrierOwnerId ?? 0);
    const isDocumentsRootView =
      !scope &&
      projectIds.length === 0 &&
      programIds.length === 0 &&
      intervenantIds.length === 0 &&
      categoryIds.length === 0 &&
      selectedTypes.length === 0 &&
      !(params.get("folderIds") ?? "") &&
      !(params.get("origin") ?? "");

    if (!currentOwnerId) {
      return null;
    }

    if (isDocumentsRootView) {
      return {
        scopeType: "porteur",
        scopeId: currentOwnerId,
        defaultKey: ROOT_FILES_DEFAULT_KEY,
        folderName: "Fichiers",
      };
    }

    if (projectIds.length === 1) {
      const projectScopeId = projectIds[0]!;

      if (selectedTypes.includes("image")) {
        return {
          scopeType: "project",
          scopeId: projectScopeId,
          defaultKey: "photos",
          folderName: "Photos",
        };
      }

      if (categoryIds.length === 1 && PROJECT_DOC_TYPES.includes(categoryIds[0]!)) {
        return {
          scopeType: "project",
          scopeId: projectScopeId,
          defaultKey: `type-${categoryIds[0]}`,
          folderName: getDocumentTypeLabel(categoryIds[0]!),
        };
      }

      return {
        scopeType: "project",
        scopeId: projectScopeId,
        defaultKey: "root",
        folderName: project.find((item: any) => Number(item?.value) === projectScopeId)?.label ?? "Projet",
      };
    }

    if (intervenantIds.length === 1) {
      const intervenantScopeId = intervenantIds[0]!;

      if (categoryIds.length === 1 && INTERVENANT_DOC_TYPES.includes(categoryIds[0]!)) {
        return {
          scopeType: "intervenant",
          scopeId: intervenantScopeId,
          defaultKey: `type-${categoryIds[0]}`,
          folderName: getDocumentTypeLabel(categoryIds[0]!),
        };
      }

      if (hasSameValues(categoryIds, CONTRACTUAL_DOC_TYPES)) {
        return {
          scopeType: "intervenant",
          scopeId: intervenantScopeId,
          defaultKey: "contractuel",
          folderName: "Contractuel",
        };
      }

      if (hasSameValues(categoryIds, REPORT_DOC_TYPES)) {
        return {
          scopeType: "intervenant",
          scopeId: intervenantScopeId,
          defaultKey: "rapport",
          folderName: "Rapport",
        };
      }

      return {
        scopeType: "intervenant",
        scopeId: intervenantScopeId,
        defaultKey: "root",
        folderName: "Intervenant",
      };
    }

    if (scope === "projects" && programIds.length === 0) {
      return {
        scopeType: "porteur",
        scopeId: currentOwnerId,
        defaultKey: "projects-root",
        folderName: "Mes Programmes",
      };
    }

    if (scope === "intervenants") {
      return {
        scopeType: "porteur",
        scopeId: currentOwnerId,
        defaultKey: "intervenants-root",
        folderName: "Dossier intervenant",
      };
    }

    if (programIds.length === 1 && !params.get("projects")) {
      const matchingProgram = data?.find((program) => Number(program?.id) === programIds[0]);
      return {
        scopeType: "porteur",
        scopeId: currentOwnerId,
        defaultKey: `${PROGRAM_ROOT_DEFAULT_KEY_PREFIX}${programIds[0]}`,
        folderName: matchingProgram?.name ?? "Programme",
      };
    }

    if (categoryIds.length === 1 && INTERVENANT_DOC_TYPES.includes(categoryIds[0]!)) {
      return {
        scopeType: "porteur",
        scopeId: currentOwnerId,
        defaultKey: `type-${categoryIds[0]}`,
        folderName: getDocumentTypeLabel(categoryIds[0]!),
      };
    }

    if (hasSameValues(categoryIds, CONTRACTUAL_DOC_TYPES)) {
      return {
        scopeType: "porteur",
        scopeId: currentOwnerId,
        defaultKey: "contractuel",
        folderName: "Contractuel",
      };
    }

    if (hasSameValues(categoryIds, REPORT_DOC_TYPES)) {
      return {
        scopeType: "porteur",
        scopeId: currentOwnerId,
        defaultKey: "rapport",
        folderName: "Rapport",
      };
    }

    if (scope === "porteur") {
      return {
        scopeType: "porteur",
        scopeId: currentOwnerId,
        defaultKey: "root",
        folderName: PORTEUR_ROOT_DEFAULT_LABEL,
      };
    }

    return {
      scopeType: "porteur",
      scopeId: currentOwnerId,
      defaultKey: "root",
      folderName: PORTEUR_ROOT_DEFAULT_LABEL,
    };
  };

  const ensureCurrentDefaultFolderId = async (
    target = resolveCurrentDefaultFolderTarget(),
  ): Promise<number | null> => {

    if (!target) {
      return null;
    }

    const scopedFolders = customFolders?.[target.scopeType]?.[String(target.scopeId)] ?? [];
    const existingFolder = findFolderByDefaultKey(scopedFolders, target.defaultKey);
    if (existingFolder?.id) {
      return Number(existingFolder.id);
    }

    const savedTree = await saveFolderAsync({
      endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/save`,
      body: {
        scopeType: target.scopeType,
        scopeId: target.scopeId,
        defaultKey: target.defaultKey,
        reuseByDefaultKey: true,
        name: target.folderName,
      },
    });

    await saveFolderAsync({
      endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/default/hide`,
      body: {
        scopeType: target.scopeType,
        scopeId: target.scopeId,
        defaultKey: target.defaultKey,
      },
    });

    const savedScopedFolders =
      (savedTree as DocumentFolderTreeResponse | undefined)?.[target.scopeType]?.[String(target.scopeId)] ?? [];
    const savedFolder = findFolderByDefaultKey(savedScopedFolders, target.defaultKey);

    return Number(savedFolder?.id ?? 0) || null;
  };

  const applyPrefillFromFilters = () => {
    const params = new URLSearchParams(window.location.search);
    const projectsParam = params.get("projects");
    const categoriesParam = params.get("categories");
    const folderIdsParam = params.get("folderIds");

    if (projectsParam) {
      const projectIds = projectsParam
        .split(",")
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);

      setProjectId(projectIds.length === 1 ? projectIds[0] : null);
    } else {
      setProjectId(null);
    }

    if (categoriesParam) {
      const categoryIds = categoriesParam
        .split(",")
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);

      setDocumentType(categoryIds.length === 1 ? categoryIds[0] : null);
    } else {
      setDocumentType(null);
    }

    if (folderIdsParam) {
      const folderIds = folderIdsParam
        .split(",")
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);

      setDocumentFolderId(folderIds.length === 1 ? folderIds[0] : null);
    } else {
      setDocumentFolderId(null);
    }
  };

  const getInvalidFiles = (files: File[]) =>
    files.filter((file) => {
      const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
      return !ALLOWED_EXTENSIONS.includes(extension);
    });

  const getValidFiles = (files: File[]) => {
    const invalidFiles = getInvalidFiles(files);
    return files.filter((file) => !invalidFiles.includes(file));
  };

  const showInvalidFileToast = (files: File[]) => {
    if (files.length === 0) return;

    toast({
      title:
        files.length === 1
          ? "1 fichier a un type invalide."
          : `${files.length} fichiers ont un type invalide.`,
      description:
        files.length === 1
          ? "Ce fichier n’a pas été ajouté. Les autres fichiers valides seront importés."
          : "Ces fichiers n’ont pas été ajoutés. Les autres fichiers valides seront importés.",
      variant: "destructive",
      duration: 4000,
    });
  };

  const isCarrierDocumentsQueryKey = (queryKey: readonly unknown[]) =>
    Array.isArray(queryKey) &&
    typeof queryKey[1] === "string" &&
    queryKey[1].includes("/docs/carrier");

  const getCarrierDocumentsSnapshots = (): CacheSnapshot => {
    const queries = queryClient.getQueryCache().findAll({
      predicate: (query) =>
        isCarrierDocumentsQueryKey(query.queryKey),
    });

    return queries.map((query) => ({
      queryKey: query.queryKey,
      data: queryClient.getQueryData(query.queryKey),
    }));
  };

  const restoreCarrierDocumentsSnapshots = (snapshots: CacheSnapshot) => {
    snapshots.forEach((snapshot) => {
      queryClient.setQueryData(snapshot.queryKey, snapshot.data);
    });
  };

  const addOptimisticDocuments = (
    newMedias: any[],
    meta: any,
    resolvedFolderId: number | null,
    resolvedFolderMeta: DocumentFolderMeta | null,
    resolvedProjectId: number | null,
  ): CacheSnapshot => {
    const snapshots = getCarrierDocumentsSnapshots();
    const optimisticDocuments = newMedias.map((media: any) => ({
      id: Number(media?.id ?? Date.now()),
      name: media?.name ?? media?.fileName ?? "document",
      path: media?.path ?? media?.uri ?? "",
      pp: Number(carrierOwnerId ?? user?.id ?? 0),
      program: project.find((item) => Number(item.value) === Number(resolvedProjectId))?.groupId ?? 0,
      project: resolvedProjectId ?? 0,
      origin: resolvedFolderId && !resolvedProjectId ? "OTHER_DOCUMENT" : "PROGRAM",
      type: Number(meta.subType),
      subType: Number(meta.subType),
      description: meta.comment,
      at: { date: new Date().toISOString() },
      ...(resolvedFolderId ? { documentFolderIds: [resolvedFolderId] } : {}),
      ...(resolvedFolderMeta
        ? {
          documentFolders: [{
            id: resolvedFolderMeta.id,
            name: resolvedFolderMeta.name,
            defaultKey: resolvedFolderMeta.defaultKey,
            scopeType: resolvedFolderMeta.scopeType,
            scopeId: resolvedFolderMeta.scopeId,
          }],
        }
        : {}),
    }));

    queryClient.setQueriesData(
      {
        predicate: (query) =>
          isCarrierDocumentsQueryKey(query.queryKey),
      },
      (previous: any) => {
        if (!previous) return previous;

        if (Array.isArray(previous)) {
          return [...optimisticDocuments, ...previous];
        }

        if (typeof previous === "object") {
          const entries = Object.entries(previous);
          const firstArrayKey = entries.find(([, value]) => Array.isArray(value))?.[0] ?? "documents";
          return {
            ...previous,
            [firstArrayKey]: [
              ...optimisticDocuments,
              ...(Array.isArray(previous[firstArrayKey]) ? previous[firstArrayKey] : []),
            ],
          };
        }

        return previous;
      },
    );

    return snapshots;
  };

  const resetForm = () => {
    setFilesToUpload([]);
    setDocumentType(null);
    setComment("");
    setDocumentFolderId(null);
    if (forceProjectSelection) {
      setProjectId(null);
    }
  };

  useEffect(() => {
    if (!externalFiles || externalFiles.length === 0) {
      return;
    }

    const invalidFiles = getInvalidFiles(externalFiles);
    if (invalidFiles.length > 0) {
      showInvalidFileToast(invalidFiles);
    }

    const validFiles = getValidFiles(externalFiles);
    if (validFiles.length === 0) {
      onExternalFilesConsumed?.();
      return;
    }

    applyPrefillFromFilters();
    setFilesToUpload(validFiles);
    setOpenAdd(true);
    onExternalFilesConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFiles]);

  const handleFileChange = async (value: any, meta: any) => {
    if (value) {
      const selectedFiles = Array.from(value) as File[];
      const invalidFiles = getInvalidFiles(selectedFiles);
      if (invalidFiles.length > 0) {
        showInvalidFileToast(invalidFiles);
      }

      const validFiles = getValidFiles(selectedFiles);
      if (validFiles.length === 0) {
        return;
      }

      setLoader(true);
      let uploadOptimisticSnapshots: CacheSnapshot | null = null;
      const uploadToast = showDocumentUploadInProgressToast({
        fileCount: validFiles.length,
        fileName: validFiles[0]?.name,
      });
      const rollbackUploadOptimism = () => {
        if (!uploadOptimisticSnapshots) {
          return;
        }

        restoreCarrierDocumentsSnapshots(uploadOptimisticSnapshots);
        uploadOptimisticSnapshots = null;
      };

      try {
        const currentParams = new URLSearchParams(window.location.search);
        const routeProjectId = getFirstPositiveNumberParam(currentParams, "projects");
        const routeFolderId = getFirstPositiveNumberParam(currentParams, "folderIds");
        const routeCategoryId = getFirstPositiveNumberParam(currentParams, "categories");
        const routeTypes = (currentParams.get("types") ?? "")
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean);
        const effectiveProjectId = routeProjectId ?? projectId;
        let resolvedDocumentFolderId = routeFolderId ?? documentFolderId;
        let resolvedDefaultFolderTarget: ResolvedDefaultFolderTarget | null = null;
        const isProjectDefaultRoute =
          Boolean(effectiveProjectId) &&
          !routeFolderId &&
          (
            (null !== routeCategoryId && PROJECT_DOC_TYPES.includes(routeCategoryId)) ||
            routeTypes.includes("image")
          );

        if (!resolvedDocumentFolderId && !isProjectDefaultRoute) {
          resolvedDefaultFolderTarget = resolveCurrentDefaultFolderTarget();
          const isProjectRouteTarget =
            resolvedDefaultFolderTarget?.scopeType === "project" &&
            Number(resolvedDefaultFolderTarget.scopeId) === Number(effectiveProjectId);
          if (!effectiveProjectId || isProjectRouteTarget) {
            resolvedDocumentFolderId = await ensureCurrentDefaultFolderId(resolvedDefaultFolderTarget);
          }
        }
        const resolvedFolderMeta = resolveFolderMeta(resolvedDocumentFolderId, resolvedDefaultFolderTarget);

        if (!effectiveProjectId && !resolvedDocumentFolderId) {
          uploadToast.dismiss();
          toast({
            title: "Veuillez sélectionner un projet ou ouvrir un dossier.",
            variant: "destructive",
            duration: 4000,
          });
          setLoader(false);
          return;
        }

        const optimisticUploadStartedAt = Date.now();
        uploadOptimisticSnapshots = addOptimisticDocuments(
          validFiles.map((file, index) => ({
            id: -(optimisticUploadStartedAt + index + 1),
            name: file.name,
            path: "",
            isOptimistic: true,
            isUploading: true,
          })),
          meta,
          resolvedDocumentFolderId,
          resolvedFolderMeta,
          effectiveProjectId,
        );

        const data = new FormData();
        const type = 2;
        data.append("type", String(type));
        data.append("subType", meta.subType);
        data.append("description", meta.comment);

        for (const file of validFiles) {
          const fileDefaultName = file.name;
          let compressedBlob: Blob | null = null;
          if (file.id == undefined) {
            compressedBlob = (await handleCompressImage(file, 1024, 768, 0.9)) as Blob | null;
          }
          if (compressedBlob) {
            const renamedFile = new File(
              [compressedBlob], // contenu
              fileDefaultName, // nouveau nom
              { type: compressedBlob.type }, // garder le type MIME d'origine
            );
            data.append("files[]", renamedFile);
          } else {
            data.append("files[]", file);
          }
        }

        const newMedias = await postMultipart({
          url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
          data,
        });
        if (
          newMedias &&
          !Array.isArray(newMedias) &&
          newMedias.success !== undefined &&
          newMedias.success !== null &&
          !newMedias.success
        ) {
          uploadToast.dismiss();
          showDocumentUploadErrorToast({
            fileCount: validFiles.length,
            fileName: validFiles[0]?.name,
            message: newMedias.message,
          });

          rollbackUploadOptimism();
          setLoader(false);
          return;
        }
        if (!Array.isArray(newMedias)) {
          uploadToast.dismiss();
          showDocumentUploadErrorToast({
            fileCount: validFiles.length,
            fileName: validFiles[0]?.name,
            message: "Erreur lors de l’ajout du document.",
          });
          rollbackUploadOptimism();
          setLoader(false);
          return;
        }

        const newMediasWithComment = newMedias.map((media: any) =>
          withDocumentComment(media, meta.comment),
        );
        rememberDocumentComments(newMediasWithComment, meta.comment);
        rollbackUploadOptimism();
        const optimisticSnapshots = addOptimisticDocuments(
          newMediasWithComment,
          meta,
          resolvedDocumentFolderId,
          resolvedFolderMeta,
          effectiveProjectId,
        );

        try {
          if (effectiveProjectId) {
            const projectselected = project?.find((p) => Number(p.value) === Number(effectiveProjectId));
            const newData = {
              files: (projectselected?.media ?? [])
                .map((media: any) => media.id)
                .concat(newMediasWithComment.map((media: any) => media?.id)),
            };

            await addFile({
              endpoint: `${import.meta.env.VITE_API_ENDPOINT}/project/edit/${effectiveProjectId}`,
              body: {
                ...newData,
                ...(resolvedDocumentFolderId ? { documentFolderId: resolvedDocumentFolderId } : {}),
                ...(resolvedDocumentFolderId ? { otherDocumentMediaIds: newMediasWithComment.map((media: any) => media?.id) } : {}),
              },
            });
          } else if (resolvedDocumentFolderId) {
            await addFile({
              endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/attach-media`,
              body: {
                folderId: resolvedDocumentFolderId,
                mediaIds: newMediasWithComment.map((media: any) => media?.id),
                description: meta.comment,
              },
            });
          }
        } catch (error) {
          restoreCarrierDocumentsSnapshots(optimisticSnapshots);
          throw error;
        }

        const [freshTree, freshDocuments] = await Promise.all([
          fetchJsonWithAuth<DocumentFolderTreeResponse>(documentFoldersTreeEndpoint),
          fetchJsonWithAuth<any>(carrierDocumentsEndpoint),
        ]);

        if (freshTree) {
          queryClient.setQueryData(["fetchDataV2", documentFoldersTreeEndpoint], freshTree);
        }

        if (freshDocuments) {
          const freshDocumentsWithComments = applyRememberedDocumentComments(freshDocuments);
          queryClient.setQueriesData(
            {
              predicate: (query) => isCarrierDocumentsQueryKey(query.queryKey),
            },
            freshDocumentsWithComments,
          );
        }

        setLoader(false);
        if (forceProjectSelection) {
          setProjectId(null);
        }
        setDocumentFolderId(null);
        setOpenAdd(false);
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[1] === "string" &&
            (isCarrierDocumentsQueryKey(query.queryKey)
              || query.queryKey[1].includes("/document-folders/tree")),
        });
        uploadToast.dismiss();
        showDocumentUploadSuccessToast({
          fileCount: validFiles.length,
          fileName: validFiles[0]?.name,
        });
      } catch (_error) {
        rollbackUploadOptimism();
        uploadToast.dismiss();
        showDocumentUploadErrorToast({
          fileCount: validFiles.length,
          fileName: validFiles[0]?.name,
          message: "Erreur lors de l’ajout du document.",
        });
        setLoader(false);
      }
    }
  };

  return (
    <div>
      {!hideTrigger && (
        <Button
          onClick={() => {
            applyPrefillFromFilters();
            setOpenAdd(true);
          }}
        >
          Ajouter un document
        </Button>
      )}
      <Dialog
        open={openAdd}
        onOpenChange={(nextOpen) => {
          if (loader) {
            return;
          }

          setOpenAdd(nextOpen);
          if (!nextOpen) {
            resetForm();
          }
        }}
      >
        <DialogContent>
          <div className="space-y-4">
            {/*
              Sélection de projet masquée à la demande.
              Le projet reste déterminé automatiquement depuis le contexte courant.
            */}
            {false && (showProjectSelection || forceProjectSelection || (!tab || tab != "document")) && (
              <div>
                <label className="text-sm font-medium">
                  Projet {forceProjectSelection && <span className="text-red-600">*</span>}
                </label>
                <ProjectCombobox
                  value={projectId}
                  onChange={setProjectId}
                  project={scopedProjects}
                  data={data ?? []}
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium">
                Type de document <span className="text-red-600">*</span>
              </label>
              <DocumentTypeCombobox
                value={documentType}
                onChange={setDocumentType}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Commentaire</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full border p-2 rounded"
                placeholder="Ajouter un commentaire (facultatif)"
              >
                {comment}
              </textarea>
            </div>

            <div>
              <FileDropzone
                accept={textTypes}
                onFileSelected={setFilesToUpload}
                selectedFiles={filesToUpload}
                onInvalidFiles={showInvalidFileToast}
              />
            </div>

            <button
              className="bg-[#1d5215] text-white px-4 py-2 rounded disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
              onClick={async () => {
                if (filesToUpload.length === 0 || !documentType) {
                  toast({
                    title: "Veuillez choisir un type de document et un fichier.",
                    variant: "destructive",
                    duration: 4000,
                  });
                  return;
                }

                if (forceProjectSelection && !projectId) {
                  toast({
                    title: "Veuillez sélectionner un projet.",
                    variant: "destructive",
                    duration: 4000,
                  });
                  return;
                }

                const normalizedFiles = filesToUpload.map(
                  (f) => new File([f], f.name, { type: f.type }),
                );

                setOpenAdd(false);
                void handleFileChange(normalizedFiles, {
                  subType: documentType,
                  comment,
                }).finally(() => {
                  resetForm();
                });
              }}
              disabled={loader || !documentType}
            >
              {loader ? <LoadingSpinner /> : "Ajouter"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddDocuments;
